import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import {
  HARD_INEQUALITY,
  MATERIAL_SYMBOL,
  SLOT_WIDTH,
  SOFT_INEQUALITY,
} from "./MaterialLimitInequality";

const SLOT_HALF = SLOT_WIDTH / 2;

const ExampleSlot = () => (
  <Box
    sx={{
      width: SLOT_WIDTH,
      height: 22,
      borderBottom: 1,
      borderColor: "text.disabled",
    }}
  />
);

const Symbol = ({ text }: { text: string }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      fontWeight:
        text === HARD_INEQUALITY ? "fontWeightBold" : "fontWeightRegular",
    }}
  >
    {text}
  </Typography>
);

const Help = ({ title }: { title: string }) => (
  <Tooltip title={title}>
    <HelpOutlineIcon
      fontSize="small"
      sx={{ color: "text.secondary", cursor: "help" }}
    />
  </Tooltip>
);

const Marker = ({ colour, label }: { colour: string; label: string }) => (
  <Stack direction="row" gap={0.5} alignItems="center">
    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colour }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const braceSx = {
  borderTop: 1,
  borderLeft: 1,
  borderRight: 1,
  borderColor: "text.disabled",
  borderTopLeftRadius: 6,
  borderTopRightRadius: 6,
  alignSelf: "stretch",
  justifySelf: "stretch",
} as const;

const cellSx = {
  gridRowStart: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

/** A static, annotated example of a limit cell, explaining its parts on hover. */
export const MaterialLimitLegend = () => {
  const { t } = useTenantTranslation();
  return (
    <Stack
      gap={1}
      justifyContent="space-between"
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle2">
        {t("materialLimitLegendTitle")}
      </Typography>
      <Stack direction="row" gap={2.5} alignItems="flex-end" flexWrap="wrap">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${SLOT_HALF}px ${SLOT_HALF}px auto ${SLOT_HALF}px ${SLOT_HALF}px auto auto auto ${SLOT_HALF}px ${SLOT_HALF}px auto ${SLOT_HALF}px ${SLOT_HALF}px`,
            gridTemplateRows: "auto 6px auto 6px auto auto",
            columnGap: 0.5,
            justifyItems: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ gridRow: 1, gridColumn: "2 / 13", justifySelf: "center" }}>
            <Help title={t("materialLimitHelpHard")} />
          </Box>
          <Box sx={{ ...braceSx, gridRow: "2 / 5", gridColumn: "2 / 13" }} />
          <Box sx={{ gridRow: 3, gridColumn: "5 / 10", justifySelf: "center" }}>
            <Help title={t("materialLimitHelpSoft")} />
          </Box>
          <Box sx={{ ...braceSx, gridRow: "4 / 5", gridColumn: "5 / 10" }} />

          <Box sx={{ ...cellSx, gridColumn: "1 / 3" }}>
            <ExampleSlot />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: 3 }}>
            <Symbol text={HARD_INEQUALITY} />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: "4 / 6" }}>
            <ExampleSlot />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: 6 }}>
            <Symbol text={SOFT_INEQUALITY} />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: 7 }}>
            <Symbol text={MATERIAL_SYMBOL} />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: 8 }}>
            <Symbol text={SOFT_INEQUALITY} />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: "9 / 11" }}>
            <ExampleSlot />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: 11 }}>
            <Symbol text={HARD_INEQUALITY} />
          </Box>
          <Box sx={{ ...cellSx, gridColumn: "12 / 14" }}>
            <ExampleSlot />
          </Box>

          <Box sx={{ gridRow: 6, gridColumn: 7, justifySelf: "center" }}>
            <Help title={t("materialLimitHelpAmount")} />
          </Box>
        </Box>
        <Stack direction="row" gap={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t("hardness")}
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: "primary.main",
              borderRadius: 1,
              px: 0.5,
              display: "inline-flex",
            }}
          >
            <ExampleSlot />
          </Box>
          <Help title={t("materialLimitHelpHardness")} />
        </Stack>
      </Stack>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Marker colour="info.main" label={t("boundsInOtherScopes")} />
        <Marker colour="secondary.main" label={t("differsFromDefault")} />
        <Marker colour="error.main" label={t("impossibleCombination")} />
        <Marker colour="warning.main" label={t("materialExcludedByLimit")} />
        <Stack direction="row" gap={0.5} alignItems="center">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: "warning.light",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("edited")}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
